/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import uuid from 'uuid/v4';
import { withRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import HistoryHelper from '@Services/HistoryHelper';
import NavigationHistory from '@Services/NavigationHistory';
import StructureThumbnailContainer from '@Containers/StructureThumbnail';
import { compose } from 'redux';
import { withCombinedRouteParams } from '@AppRoutes/ConnectedRoute';
import values from 'lodash/values';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import { open as openStructureDialog } from '@Containers/StructureDialog/StructureDialogAction';
import { updateDialog, closeDialog } from '@Containers/Dialogs/DialogsActions';
import { ButtonWithIcon, Button } from '@els/els-react--button';
import { dispatchCreateAlert } from '@Pages/Alerts/AlertsComponents/AlertCreateDialog/AlertCreateDialogActions';
import { isAlertVisible } from '@Pages/Alerts/AlertsUtils';
import parseV3000SumFormula from '@Utils/React/MolFiletoFormula';
import { recordCountOrTotalResultFromHistoryItem } from '@Containers/PatentFamilyClustering/selectors';
import { historyReducerKey, typeOfHistorySaveDialog } from '../Constants';
import { fetchRecentHistory, handleViewSaveModal } from '../HistoryAction';
import { capitalizeFirstLetter, todayDateFilter } from '../utils';

function HistoryRecent() {
  const { recentHistory, historyTypes, isRecentHistoryLoaded, showSaveModal } = useSelector((state) => state.get(historyReducerKey));
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchRecentHistory(historyTypes));
    return () => {
      dispatch(closeDialog({ id: typeOfHistorySaveDialog }));
    };
  }, []);

  useEffect(() => {
    if (showSaveModal) {
      dispatch(
        updateDialog({
          id: typeOfHistorySaveDialog,
          dialogType: typeOfHistorySaveDialog,
        })
      );
    } else {
      dispatch(closeDialog({ id: typeOfHistorySaveDialog }));
    }
  }, [showSaveModal]);

  const isTypeSupported = (historyType) => historyTypes && values(historyTypes)?.indexOf(historyType) !== -1;
  if (!isRecentHistoryLoaded) {
    return (
      <div className="history-page__container">
        <div className="query__group">
          <RxSpinnerComponent autoStart spinnerScale={1} spinnerId="history" />
        </div>
      </div>
    );
  }

  return (
    <div className="history-page__container">
      {recentHistory.length > 0 ? (
        <div className="query__group">
          {recentHistory.map((groupItem, groupItemIndex) => (
            <section key={`groupItem${groupItem.databaseName}`}>
              <h2 className="history__dbs__name">{groupItem.databaseName}</h2>
              {groupItem.items &&
                groupItem.items
                  .sort((x, y) => y.creationTime - x.creationTime)
                  .map((item, index) => (
                    <article
                      key={`historyItem${groupItem.databaseName}${item.id}`}
                      className={`query__item query__item--recent query__item--${groupItem.databaseName === 'Commercial Substances' ? 'commercialsubstance' : item.context} query__item__row`}
                      title={`${item.comment}`}>
                      <header className="query__item__header">
                        <h3 className="query__item__title u-els-font-size-h4">
                          {`${recordCountOrTotalResultFromHistoryItem(item)} ${
                            item.context ? capitalizeFirstLetter(item.context?.replace(/"/g, '')) : ''
                          }`}
                        </h3>
                        <time className="query__item__time">{item.creationTime && todayDateFilter(item.creationTime)}</time>
                      </header>

                      <section className="query__item__content">
                        <div className="query__item__description">
                          <div>
                            <div className="crumb-comment">
                              {item.batchSearch && (
                                <span className="rx-btn rx-btn--small rx-btn--basic_link rx-btn--right pull-right">
                                  <span className="rx-btn-text">
                                    <span className="rx-btn-text-container">Execution details</span>
                                  </span>
                                  <span className="rx-btn-icon">
                                    <i className="ss-navigateright button-icon" />
                                  </span>
                                </span>
                              )}
                              <span>{item.comment}</span>
                            </div>
                            {item.quickSearchItem && item.quickSearchItem.structure && (
                              <Button
                                data-e2e="history-structure"
                                type="secondary"
                                className="u-els-padding-1o4 e2e-history-structure"
                                onClick={() => {
                                  dispatch(
                                    openStructureDialog(uuid(), item.quickSearchItem.structure, {
                                      name: parseV3000SumFormula(item.quickSearchItem.structure),
                                      isLinearFormula: true,
                                    })
                                  );
                                }}>
                                <StructureThumbnailContainer
                                  enableThumbnail
                                  structure={item.quickSearchItem.structure}
                                  thumbnailType="history"
                                />
                              </Button>
                            )}
                            {item.structures && item.structures?.length > 0 && (
                              <Button
                                data-e2e="history-structure"
                                type="secondary"
                                key={`structure${uuid()}`}
                                className="u-els-padding-1o4 e2e-history-structure"
                                onClick={() => {
                                  dispatch(
                                    openStructureDialog(uuid(), item.structures, {
                                      name: parseV3000SumFormula(item.structure),
                                      isLinearFormula: true,
                                    })
                                  );
                                }}>
                                <StructureThumbnailContainer
                                  enableThumbnail
                                  structure={item.structure || item.structures?.[0]?.structure}
                                  thumbnailType="history"
                                />
                              </Button>
                            )}
                          </div>
                        </div>
                      </section>
                      <footer className="query__item__options">
                        <div className="rx-btn-wrap rx-btn-group--right history-btn-group">
                          <ButtonWithIcon
                            data-e2e="history-recent-edit-query"
                            id="history-recent-edit-query"
                            name="edit-query"
                            type="link"
                            sprite="DuplicateDocument"
                            linkIconSize="xs"
                            iconRight
                            disabled={
                              item.historyType !== historyTypes.QUERY_BUILDER &&
                              !(item.quickSearchItem && item.quickSearchItem.whereClauseImportToken)
                            }
                            onClick={() => HistoryHelper.copyQuery(item)}
                            className="e2e-history-recent-edit-query">
                            Edit query
                          </ButtonWithIcon>

                          <ButtonWithIcon
                            data-e2e="history-recent-save"
                            id="history-recent-save"
                            name="save"
                            type="link"
                            sprite="SaveFile"
                            linkIconSize="xs"
                            iconRight
                            disabled={!isTypeSupported(item.historyType)}
                            onClick={() => dispatch(handleViewSaveModal(true, true, groupItemIndex, index, { ...item }))}
                            className="e2e-history-recent-save">
                            Save
                          </ButtonWithIcon>
                          {isAlertVisible(item) && (
                            <ButtonWithIcon
                              data-e2e="history-recent-open-alert"
                              id="history-recent-open-alert"
                              name="alert"
                              type="link"
                              sprite="Alarm"
                              linkIconSize="xs"
                              iconRight
                              onClick={() => dispatchCreateAlert(item)}
                              className="e2e-history-recent-open-alert">
                              Alert
                            </ButtonWithIcon>
                          )}
                        </div>

                        <ButtonWithIcon
                          data-e2e="history-recent-open-view"
                          id="history-recent-open-view"
                          name="view"
                          type="tertiary"
                          sprite="ChevronRight"
                          iconRight
                          disabled={item.resultSize === 0}
                          onClick={() => NavigationHistory.viewHistory(item)}
                          className="e2e-history-recent-open-view">
                          View
                        </ButtonWithIcon>
                      </footer>
                    </article>
                  ))}
            </section>
          ))}
        </div>
      ) : (
        <div className="query__group">No recent searches found...</div>
      )}
    </div>
  );
}

const EnhancedHistoryRecent = compose(withCombinedRouteParams, withRouter)(HistoryRecent);
export default EnhancedHistoryRecent;
